/**
 * Created by katarinababic on 22.11.24.
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import { BreathingEntry } from '../../../Model/Explore/BreathingEntry';
import { useRootStore } from '../../../Store/useRootStore';
import { useLocalized } from '../../../Hooks/useLocalized';
import { useTrans } from '../../../Store/System/LocalizationStore';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { MarkdownRenderer } from '../../Markdown/MarkdownRenderer';
import { BreathingExerciseDurationSelection } from './BreathingExerciseDurationSelection';
import { BreathingSession, createBreathingSession } from '../../../Model/Explore/BreathingSession';
import { BreathingExerciseBackgroundSound } from './BreathingExerciseBackgroundSound';
import { BreathingExerciseProgress } from './BreathingExerciseProgress';
import { useEffect, useRef } from 'react';
import { reaction, runInAction } from 'mobx';
import { usePlausible } from '../../../Contexts/PlausibleContext';
import { saveActivityLogDirect } from '../../../Model/Activity/ActivityLog';
import { ScheduleButton } from '../../ScheduleButton';
import { ShareButton } from '../../ShareButton';
import { ScheduleModal } from '../../ScheduleModal/ScheduleModal';
import { useContentMetaTags } from '../../../Hooks/useContentMetaTags';
import { useEventParent } from '../../../Hooks/useEventEntryMutations';
import { Theme } from '../../../Theme/Theme';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageContainer = styled.div`
  min-height: 100vh;
  background: #fafafa;
  padding: 32px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-width: 1200px;
  margin: 0 auto;
`;

const PlayerSection = styled.div`
  width: 100%;
`;

const ContentCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  background: white;
  border-radius: 24px;
  padding: 32px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  animation: ${fadeIn} 0.6s ease-out;

  @media (max-width: 768px) {
    padding: 24px;
  }
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Title = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: ${Theme.Colors.textColor};
  margin: 0;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const GradientLine = styled.div`
  width: min(200px, 40%);
  height: 4px;
  border-radius: 4px;
  background: ${Theme.Colors.primary};
`;

const Subtitle = styled.h4`
  font-size: 1rem;
  font-weight: 500;
  color: ${Theme.Colors.charcoal};
  margin: 0;
`;

const Description = styled.div`
  font-size: 1rem;
  line-height: 1.7;
  color: ${Theme.Colors.charcoal};
`;

const OptionsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ActionSection = styled.div`
  padding-top: 8px;
`;

export type ExploreContentViewBreathingProps = {
  entry: BreathingEntry;
};

export const ExploreContentViewBreathing: React.FC<ExploreContentViewBreathingProps> = observer(({ entry }) => {
  const { language, analytics, authentication, engagement } = useRootStore();
  const { t } = useTrans();
  const { trackPlausible } = usePlausible();

  const title = useLocalized(entry.name);
  const technique = React.useMemo(() => entry.technique.map((t) => t.duration).join('-') || '4-4-4', [entry]);
  const description = useLocalized(entry.description);

  useContentMetaTags({ title, description });

  const [breathingSession, setBreathingSession] = React.useState<BreathingSession>(() => createBreathingSession(entry));
  const [showSchedule, setShowSchedule] = React.useState(false);
  const backgroundSoundRef = React.useRef<HTMLAudioElement | null>(null);
  const sessionStartTimeRef = useRef<number | null>(null);

  const { data: eventEntry, refetch: refetchEvent } = useEventParent(entry.type, entry.id);

  React.useEffect(() => {
    trackPlausible('Page View', { pageTitle: title, contentType: entry.type, contentId: entry.id });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry.id, title, entry.type]);

  const handleShowScheduleModal = React.useCallback(() => setShowSchedule(true), []);
  const handleCloseScheduleModal = React.useCallback(() => {
    setShowSchedule(false);
    refetchEvent();
  }, [refetchEvent]);

  const handleStartSession = React.useCallback(() => {
    runInAction(() => breathingSession.startSession());
    sessionStartTimeRef.current = Date.now();
    trackPlausible('Start Watching', { pageTitle: title, contentType: entry.type, contentId: entry.id });
  }, [breathingSession, trackPlausible, title, entry.type]);

  const recreateBreathingSession = React.useCallback(() => {
    if (breathingSession.active && sessionStartTimeRef.current) {
      const sessionDuration = Math.floor((Date.now() - sessionStartTimeRef.current) / 1000);
      trackPlausible('Pause Watching', { seconds: `${sessionDuration}s`, pageTitle: title, contentType: entry.type, contentId: entry.id });
    }
    sessionStartTimeRef.current = null;
    if (backgroundSoundRef.current) {
      backgroundSoundRef.current.pause();
      backgroundSoundRef.current.currentTime = 0;
      backgroundSoundRef.current = null;
    }
    setBreathingSession(createBreathingSession(entry));
  }, [entry, breathingSession, trackPlausible]);

  useEffect(() => {
    const soundFile = breathingSession.selectedSound?.source();

    if (backgroundSoundRef.current) {
      backgroundSoundRef.current.pause();
      backgroundSoundRef.current.currentTime = 0;
      backgroundSoundRef.current = null;
    }

    if (soundFile) {
      backgroundSoundRef.current = new Audio(soundFile);
      backgroundSoundRef.current.loop = true;
    } else {
      // if 'no-sound' is selected
      backgroundSoundRef.current = null;
    }

    if (breathingSession.active && backgroundSoundRef.current) {
      backgroundSoundRef.current.play();
    }

    return () => {
      if (backgroundSoundRef.current) {
        backgroundSoundRef.current.pause();
        backgroundSoundRef.current.currentTime = 0;
        backgroundSoundRef.current = null;
      }
    };
  }, [breathingSession.active, breathingSession.selectedSound]);

  useEffect(
    () =>
      reaction(
        () => breathingSession.completed,
        (completed) => {
          const { athlete } = authentication;
          if (completed) {
            const sessionDuration = sessionStartTimeRef.current
              ? Math.floor((Date.now() - sessionStartTimeRef.current) / 1000)
              : 0;
            trackPlausible('Content Complete', { seconds: `${sessionDuration}s`, pageTitle: title, contentType: entry.type, contentId: entry.id });
            sessionStartTimeRef.current = null;
            const activityLog = breathingSession.createActivityLog();
            if (athlete) {
              if (activityLog) {
                engagement
                  .sendActivityLogged(activityLog)
                  .then((res) => {
                    // Show the modal but don't wait for it to close
                    engagement.showPointsModalPromise(res).catch((error) => {
                      console.error('Failed to show points modal:', error);
                    });
                    // Save activity log and log analytics immediately
                    return saveActivityLogDirect(activityLog);
                  })
                  .then(() => {
                    analytics.logEvents(['breathingSession_finished', 'explore_session_finished'], {
                      id: breathingSession.entry.id,
                      type: breathingSession.entry.type,
                      totalDuration: breathingSession.totalDuration,
                      percentageCompleted: breathingSession.percentageCompleted,
                      technique: breathingSession.entry.technique,
                      timestamp: Date.now(),
                      name: title,
                    });
                  })
                  .catch((error) => {
                    console.error('Error in activity log processing:', error);
                  });
              }
            }
          }
        },
        { name: 'log breathing session' },
      ),
    [authentication, breathingSession, analytics, engagement, entry, title, trackPlausible],
  );

  return (
    <PageContainer>
      <ContentWrapper>
        <PlayerSection>
          <BreathingExerciseProgress
            activeSession={breathingSession}
            onStart={handleStartSession}
            onStop={recreateBreathingSession}
          />
        </PlayerSection>

        <ContentCard>
          <TitleContainer>
            <Title>{title}</Title>
            <GradientLine />
          </TitleContainer>

          <Subtitle>
            {t('explore.breathing.technique')}: {technique}
          </Subtitle>

          <Description>
            <MarkdownRenderer text={description} />
          </Description>

          <OptionsSection>
            {!breathingSession.active && (
              <BreathingExerciseDurationSelection breathingSession={breathingSession} />
            )}
            <BreathingExerciseBackgroundSound breathingSession={breathingSession} />
          </OptionsSection>

          <ActionSection>
            <ShareButton data={{ title, description, contentType: 'breathing', contentId: entry.id }} />
            <ScheduleButton event={eventEntry} onClick={handleShowScheduleModal} />
          </ActionSection>
        </ContentCard>
      </ContentWrapper>

      {showSchedule && (
        <ScheduleModal
          key={eventEntry?.id ?? entry.id}
          exploreEntry={entry}
          parentEvent={eventEntry}
          currentEvent={eventEntry}
          disableRecurring={false}
          onSchedule={handleCloseScheduleModal}
          onDelete={handleCloseScheduleModal}
        />
      )}
    </PageContainer>
  );
});
